/** @jsx jsx */
import React, {
  Dispatch,
  Fragment,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { jsx } from "theme-ui";
import {
  containerStyles,
  itemStyles,
  languageItemStyles,
  languageLinkActiveStyles,
  languageLinkStyles,
  languageListStyles,
  linkStyles,
  listStyles,
  navStyles,
} from "./styles";
import { Box } from "@shared-components/Box/Box";
import { Button, ButtonLink } from "@shared-components/Button/Button";
import { Link } from "@shared-components/Link/Link";
import { HamburgerIcon } from "@shared-components/AnimatableIcons/HamburgerIcon";
import { motion } from "framer-motion";
import { navItemVariants, navListVariants, navVariants } from "./animations";
import { Portal } from "@shared-components/Portal/Portal";
import {
  LinkCollection,
  LinkCollectionLink,
} from "../../carlsberg-headless/types/LinkCollection";
import { generateCbKey } from "../../shared/utils/generate-cbKey";
import { useNavigationContext } from "../../context/navigation/useNavigationContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { throttle } from "throttle-debounce";

export interface NavigationProps {
  linkCollection?: LinkCollection;
  cultureSelector?: LinkCollection;
}

export interface PureNavigationProps {
  setIsOpen?: Dispatch<boolean>;
  isOpen: boolean;
  currentCulture: string;
  linkCollection?: LinkCollection;
  cultureSelector?: LinkCollection;
  buyGiftcardLink?: LinkCollectionLink;
  bookTicketLink?: LinkCollectionLink;
}

const setWindowHeightCssVariable = () => {
  document.documentElement.style.setProperty(
    "--vh",
    `${window.innerHeight * 0.01}px`
  );
};

const getScrollbarWidth = () => {
  return window.innerWidth - document.documentElement.clientWidth;
};

const handleDisableScroll = (
  disableScroll: boolean,
  scrollY: number,
  buttonRef: RefObject<HTMLDivElement>
) => {
  const htmlTag = document.documentElement;
  const bodyTag = document.body;
  const scrollBarWidth = getScrollbarWidth();
  if (disableScroll) {
    htmlTag.style.overflow = "hidden";
    htmlTag.style.position = "fixed";
    htmlTag.style.width = "100%";
    htmlTag.style.marginTop = `-${scrollY}px`;
    bodyTag.style.marginRight = `${scrollBarWidth}px`;
    bodyTag.style.position = "relative";
    if (buttonRef.current)
      buttonRef.current.style.marginRight = `${scrollBarWidth}px`;
  }

  if (!disableScroll) {
    htmlTag.removeAttribute("style");
    bodyTag.removeAttribute("style");
    if (buttonRef.current) buttonRef.current.style.marginRight = "";
    window.scrollTo(0, scrollY);
  }
};

const PureNavigation: React.FC<PureNavigationProps> = ({
  linkCollection,
  cultureSelector,
  currentCulture,
  isOpen,
  setIsOpen,
  buyGiftcardLink,
  bookTicketLink,
}) => {
  const [scrollY, setScrollY] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleDisableScroll(isOpen, scrollY, buttonRef);
  }, [isOpen, scrollY]);

  useEffect(() => {
    const resizeHandler = throttle(100, setWindowHeightCssVariable);

    setWindowHeightCssVariable();

    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  const handleMenuToggle = () => {
    !isOpen && setScrollY(window?.scrollY);
    setIsOpen && setIsOpen(!isOpen);
  };

  return (
    <Fragment>
      <div ref={buttonRef}>
        <Button
          sx={{ border: "none", "&:focus": { boxShadow: "none" } }}
          onClick={handleMenuToggle}
          variant="burger"
          shape="round"
          title={isOpen ? "hide main navigation" : "show main navigation"}
          aria-expanded={isOpen}
        >
          <HamburgerIcon toggleIconState={isOpen} />
        </Button>
      </div>
      <Portal domElementId="navigation">
        {isOpen && (
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 1,
              top: 0,
              left: 0,
            }}
            onClick={handleMenuToggle}
          />
        )}
        <motion.div
          initial="closed"
          animate={isOpen ? "open" : "closed"}
          variants={navVariants}
          sx={containerStyles}
        >
          {linkCollection?.links && linkCollection.links?.length > 0 && (
            <Box as="nav" sx={navStyles}>
              <motion.div
                initial="closed"
                animate={isOpen ? "open" : "closed"}
                variants={navVariants}
                sx={{
                  // display: ["flex", null, "inline-flex"],
                  display: "flex",
                  gap: 4,
                  flexDirection: ["row", "column", "row"],
                  "@media screen and (min-width: 1040px) and (max-width: 1320px)":
                    {
                      flexDirection: "column",
                    },
                  marginBottom: [6, 8],
                  marginTop: [0, 2, 2],
                  alignItems: "flex-start",
                }}
              >
                {bookTicketLink?.link?.url && (
                  <ButtonLink
                    to={bookTicketLink.link.url}
                    target={
                      bookTicketLink.link.target
                        ? bookTicketLink.link.target
                        : ""
                    }
                    variant="green"
                    outline
                  >
                    {bookTicketLink.link.name}
                  </ButtonLink>
                )}
                {buyGiftcardLink?.link?.url && (
                  <ButtonLink
                    to={buyGiftcardLink.link.url}
                    target={
                      buyGiftcardLink.link.target
                        ? buyGiftcardLink.link.target
                        : ""
                    }
                    variant="green"
                    outline
                  >
                    {buyGiftcardLink.link.name}
                  </ButtonLink>
                )}
              </motion.div>
              <motion.ul
                initial="closed"
                animate={isOpen ? "open" : "closed"}
                variants={navListVariants}
                sx={listStyles}
              >
                {linkCollection.links.map(({ link, hideInNavigation }) =>
                  hideInNavigation ? null : (
                    <motion.li
                      variants={navItemVariants}
                      sx={itemStyles}
                      key={generateCbKey()}
                    >
                      <Link
                        onClick={() => (setIsOpen ? setIsOpen(false) : null)}
                        sx={linkStyles}
                        to={
                          link.relativeUrl === "" ||
                          link.relativeUrl === currentCulture
                            ? link.url
                            : link.relativeUrl.startsWith("/")
                            ? link.relativeUrl
                            : `/${link.relativeUrl}`
                        }
                        {...(link.target && { target: link.target })}
                      >
                        {link.name}
                      </Link>
                    </motion.li>
                  )
                )}
              </motion.ul>
            </Box>
          )}

          {cultureSelector && cultureSelector.links.length > 1 && (
            <Box as="ul" sx={languageListStyles}>
              {cultureSelector.links.map(({ link, culture }) => (
                <Box as="li" sx={languageItemStyles} key={generateCbKey()}>
                  <Link
                    sx={
                      culture === currentCulture
                        ? languageLinkActiveStyles
                        : languageLinkStyles
                    }
                    to={link.relativeUrl}
                    {...(link.target && { target: link.target })}
                  >
                    {link.name}
                  </Link>
                </Box>
              ))}
            </Box>
          )}
        </motion.div>
      </Portal>
    </Fragment>
  );
};

const Navigation: React.FC<NavigationProps> = ({
  linkCollection,
  cultureSelector,
}) => {
  const { isOpen, setIsOpen, ...rest } = useNavigationContext();
  const { currentCulture, buyGiftcardLink, bookTicketLink } = useSiteContext();
  const links = linkCollection ?? rest?.linkCollection;
  const cultureLinks = cultureSelector ?? rest?.cultureSelector;

  return (
    <PureNavigation
      linkCollection={links}
      cultureSelector={cultureLinks}
      currentCulture={currentCulture}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      buyGiftcardLink={buyGiftcardLink?.links?.[0]}
      bookTicketLink={bookTicketLink?.links?.[0]}
    />
  );
};

export { Navigation, PureNavigation };
